import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import styles from './EditPilotEndDialog.module.scss'

import { isApiError } from '@/api'
import { EnterprisePilotItem } from '@/api/generated'
import { updateEnterprisePilotEnd } from '@/api/internal'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import { useToast } from '@/components/Toast/ToastContext'
import { formatDateForInput, formatPilotDateLocale, getMinPilotEndDate } from '@/pages/app/Pilots/utils'

interface EditPilotEndDialogProps {
  isOpen: boolean
  pilot: EnterprisePilotItem | null
  onClose: () => void
}

const EditPilotEndDialog = ({ isOpen, pilot, onClose }: EditPilotEndDialogProps) => {
  const { updateToast } = useToast()
  const queryClient = useQueryClient()

  const [newPilotEnd, setNewPilotEnd] = useState<string>('')

  // Reset form when pilot changes
  useEffect(() => {
    if (pilot) {
      setNewPilotEnd(formatDateForInput(pilot.pilot_end))
    }
  }, [pilot])

  // Handle edge case: dialog opened without a pilot selected
  useEffect(() => {
    if (isOpen && !pilot) {
      updateToast('No pilot selected', 'error')
      onClose()
    }
  }, [isOpen, pilot, updateToast, onClose])

  const mutation = useMutation({
    mutationFn: async ({
      enterpriseId,
      pilotEnd,
      costCenterId,
    }: {
      enterpriseId: string
      pilotEnd: string
      costCenterId: string
    }) => {
      // Send plain YYYY-MM-DD date — the backend handles end-of-day conversion
      return updateEnterprisePilotEnd(enterpriseId, pilotEnd, costCenterId)
    },
    onSuccess: () => {
      updateToast('Pilot end date updated successfully', 'success')
      handleClose()
      // The pilot_end update is applied asynchronously via a downstream webhook,
      // so the backend's ['enterprise-pilots'] response may still return the old
      // value if we refetch immediately. This 1s delay is a tradeoff: it's
      // usually long enough for the webhook to land, but a slow run still shows
      // stale data, and a fast run wastes a second of UI responsiveness.
      // Revisit if the endpoint starts returning the updated value synchronously
      // (e.g. via optimistic cache update in setQueryData or server-side wait).
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['enterprise-pilots'] })
      }, 1000)
    },
    onError: (error) => {
      if (isApiError(error)) {
        updateToast(error.detail || error.message || 'Failed to update pilot end date', 'error')
      } else {
        updateToast('Failed to update pilot end date', 'error')
      }
    },
  })

  const handleClose = () => {
    setNewPilotEnd('')
    onClose()
  }

  const handleSubmit = () => {
    if (!pilot || !newPilotEnd || !pilot.cost_center_id) return
    mutation.mutate({ enterpriseId: pilot.enterprise_id, pilotEnd: newPilotEnd, costCenterId: pilot.cost_center_id })
  }

  if (!pilot) return null // unlikely to happen, but just in case

  // Guards a stale cached row fetched before this field existed — never send an
  // undefined cost_center_id, since that would silently fall back to the enterprise's
  // oldest-live-CC (possibly the wrong one for a multi-cost-center enterprise).
  const isMissingCostCenter = !pilot.cost_center_id

  return (
    <DialogWrapper isOpen={isOpen} title="Edit Pilot End Date" close={handleClose} minWidth={400}>
      <div className={styles.dialogBody}>
        <div className={styles.pilotInfo}>
          <p>
            <strong>Enterprise:</strong> {pilot.enterprise_name}
          </p>
          <p>
            <strong>Team:</strong> {pilot.cost_center_name}
          </p>
          <p>
            <strong>Current End Date:</strong> {formatPilotDateLocale(pilot.pilot_end)}
          </p>
          {isMissingCostCenter && (
            <p className={styles.hint}>
              This row is missing its team id — refresh the Pilots list before editing.
            </p>
          )}
        </div>

        <div className={styles.formField}>
          <label htmlFor="pilot-end-date">New Pilot End Date</label>
          <input
            id="pilot-end-date"
            type="date"
            value={newPilotEnd}
            onChange={(e) => setNewPilotEnd(e.target.value)}
            min={getMinPilotEndDate()}
            className={styles.dateInput}
          />
          <span className={styles.hint}>
            Date is interpreted as end of day UTC. Must be at least 24 hours in the future.
          </span>
        </div>

        <div className={styles.dialogFooter}>
          <Button title="Cancel" onClick={handleClose} type="light" isDisabled={mutation.isPending} />
          <Button
            title={mutation.isPending ? 'Saving...' : 'Save'}
            onClick={handleSubmit}
            type="dark"
            isDisabled={mutation.isPending || !newPilotEnd || isMissingCostCenter}
          />
        </div>
      </div>
    </DialogWrapper>
  )
}

export default EditPilotEndDialog
